import { useCallback, useEffect, useSyncExternalStore } from 'react';

/**
 * Client-only recent-records history backed by localStorage (§2.16).
 * TODO(§2.16-v2): move to a server-side personalisation store per user.
 */

export type RecentKind = 'candidate' | 'application' | 'job' | 'client' | 'requisition';

export type RecentRecord = {
  kind: RecentKind;
  id: string;
  label: string;
  sublabel?: string;
  to: string;
  at: number;
};

const LS_KEY = 'ggj.recent.v1';
const MAX_ITEMS = 12;
const BROADCAST_EVENT = 'ggj:recent-changed';

const EMPTY: RecentRecord[] = [];

function parseRaw(raw: string | null): RecentRecord[] {
  if (!raw) return EMPTY;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return EMPTY;
    const cleaned = parsed
      .filter((r): r is RecentRecord => {
        if (!r || typeof r !== 'object') return false;
        const obj = r as Record<string, unknown>;
        return (
          typeof obj.kind === 'string' &&
          typeof obj.id === 'string' &&
          typeof obj.label === 'string' &&
          typeof obj.to === 'string' &&
          typeof obj.at === 'number'
        );
      })
      .slice(0, MAX_ITEMS);
    return cleaned.length === 0 ? EMPTY : cleaned;
  } catch {
    return EMPTY;
  }
}

// Cached snapshot for useSyncExternalStore: keep a stable reference until the
// raw localStorage string actually changes, otherwise React sees a new array
// every render and loops (Maximum update depth exceeded).
let cachedRaw: string | null | undefined = undefined;
let cachedSnapshot: RecentRecord[] = EMPTY;

function readFromStorage(): RecentRecord[] {
  if (typeof window === 'undefined') return EMPTY;
  const raw = window.localStorage.getItem(LS_KEY);
  if (raw === cachedRaw) return cachedSnapshot;
  cachedRaw = raw;
  cachedSnapshot = parseRaw(raw);
  return cachedSnapshot;
}

function invalidateCache(): void {
  cachedRaw = undefined;
}

function writeToStorage(items: RecentRecord[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
    invalidateCache();
    window.dispatchEvent(new Event(BROADCAST_EVENT));
  } catch {
    /* ignore storage errors */
  }
}

function subscribe(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const onStorage = (e: StorageEvent) => {
    if (e.key === LS_KEY) {
      invalidateCache();
      onChange();
    }
  };
  const onBroadcast = () => {
    invalidateCache();
    onChange();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener(BROADCAST_EVENT, onBroadcast);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(BROADCAST_EVENT, onBroadcast);
  };
}

function getSnapshot(): RecentRecord[] {
  return readFromStorage();
}

function getServerSnapshot(): RecentRecord[] {
  return EMPTY;
}

export function useRecentlyViewed(): {
  items: RecentRecord[];
  add: (entry: Omit<RecentRecord, 'at'>) => void;
  remove: (kind: RecentKind, id: string) => void;
  clear: () => void;
} {
  const items = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const add = useCallback((entry: Omit<RecentRecord, 'at'>) => {
    const current = readFromStorage();
    const next: RecentRecord[] = [
      { ...entry, at: Date.now() },
      ...current.filter((r) => !(r.kind === entry.kind && r.id === entry.id)),
    ].slice(0, MAX_ITEMS);
    writeToStorage(next);
  }, []);

  const remove = useCallback((kind: RecentKind, id: string) => {
    const current = readFromStorage();
    const next = current.filter((r) => !(r.kind === kind && r.id === id));
    writeToStorage(next);
  }, []);

  const clear = useCallback(() => {
    writeToStorage([]);
  }, []);

  return { items, add, remove, clear };
}

/**
 * Call from detail pages to push the current record into Recently viewed.
 * Skips writes when any of `id` / `label` are empty (loading states).
 */
export function useTrackRecentlyViewed(entry: Omit<RecentRecord, 'at'> | null | undefined): void {
  const { add } = useRecentlyViewed();
  useEffect(() => {
    if (!entry || !entry.id || !entry.label) return;
    add(entry);
  }, [add, entry?.kind, entry?.id, entry?.label, entry?.sublabel, entry?.to]);
}
